import { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESGRisks = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRisks();
  }, []);

  const fetchRisks = async () => {
    try {
      const response = await axios.get(`${API}/esg-risks`);
      setRisks(response.data);
    } catch (error) {
      console.error('Error fetching ESG risks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'critical': return 'bg-[#A33B3B] text-white';
      case 'high': return 'bg-[#C85A32] text-white';
      case 'medium': return 'bg-[#D5CABD] text-[#111814]';
      case 'low': return 'bg-[#8B9D8F] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact.toLowerCase()) {
      case 'high': return 'text-[#A33B3B]';
      case 'medium': return 'text-[#C85A32]';
      case 'low': return 'text-[#8B9D8F]';
      default: return 'text-[#6B7280]';
    }
  };

  const getLikelihoodIcon = (likelihood) => {
    switch (likelihood.toLowerCase()) {
      case 'likely': return <AlertCircle className="w-4 h-4 text-[#C85A32]" />;
      case 'possible': return <AlertTriangle className="w-4 h-4 text-[#D5CABD]" />;
      default: return <CheckCircle className="w-4 h-4 text-[#8B9D8F]" />;
    }
  };

  const criticalRisks = risks.filter(r => r.priority.toLowerCase() === 'critical').length;
  const highRisks = risks.filter(r => r.priority.toLowerCase() === 'high').length;
  const mediumRisks = risks.filter(r => r.priority.toLowerCase() === 'medium').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="esg-risks-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          ESG Risk Identification
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Consulting View: Automated risk identification based on your ESG data patterns
        </p>
      </div>

      {/* Risk Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-[#E5E1D8]" data-testid="total-risks-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-[#6B7280]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Total Risks</span>
            </div>
            <p className="text-3xl font-bold text-[#111814]">{risks.length}</p>
          </CardContent>
        </Card>
        <Card className="border-[#A33B3B] border-2" data-testid="critical-risks-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-[#A33B3B]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#A33B3B]">Critical</span>
            </div>
            <p className="text-3xl font-bold text-[#A33B3B]">{criticalRisks}</p>
          </CardContent>
        </Card>
        <Card className="border-[#C85A32] border-2" data-testid="high-risks-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-[#C85A32]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#C85A32]">High</span>
            </div>
            <p className="text-3xl font-bold text-[#C85A32]">{highRisks}</p>
          </CardContent>
        </Card>
        <Card className="border-[#D5CABD] border-2" data-testid="medium-risks-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-[#8B9D8F]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#8B9D8F]">Medium</span>
            </div>
            <p className="text-3xl font-bold text-[#8B9D8F]">{mediumRisks}</p>
          </CardContent>
        </Card>
      </div>

      {/* Risk Register Table */}
      <Card className="border-[#E5E1D8] mb-6" data-testid="risk-register-table">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#C85A32]" />
            <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Risk Register</CardTitle>
          </div>
          <CardDescription>
            Risks automatically identified from your ESG data inputs and benchmark deviations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E1D8]">
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">ID</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Data Trigger</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Risk Description</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Impact</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Likelihood</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-widest text-[#6B7280]">Priority</th>
                </tr>
              </thead>
              <tbody>
                {risks.map((risk, index) => (
                  <tr key={index} className="border-b border-[#F2F0EB] hover:bg-[#F9F8F6] transition-colors">
                    <td className="py-3 px-4">
                      <span className="text-sm font-mono text-[#6B7280]">{risk.risk_id}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-[#C85A32] font-medium">{risk.data_trigger}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-[#111814]">{risk.risk_description}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`text-sm font-semibold ${getImpactColor(risk.impact_level)}`}>{risk.impact_level}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        {getLikelihoodIcon(risk.likelihood)}
                        <span className="text-sm text-[#4B5563]">{risk.likelihood}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={getPriorityColor(risk.priority)}>{risk.priority}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Suggested Controls */}
      <h2 className="text-2xl font-semibold tracking-tight text-[#111814] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        Suggested Controls & Mitigation
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {risks.map((risk, index) => (
          <Card 
            key={index} 
            className={`border-l-4 ${risk.priority.toLowerCase() === 'critical' ? 'border-l-[#A33B3B]' : risk.priority.toLowerCase() === 'high' ? 'border-l-[#C85A32]' : 'border-l-[#8B9D8F]'} border-[#E5E1D8]`}
            data-testid={`risk-control-${risk.risk_id}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-mono text-[#6B7280]">{risk.risk_id}</span>
                    <Badge className={getPriorityColor(risk.priority)}>{risk.priority}</Badge>
                  </div>
                  <CardTitle className="text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {risk.risk_description}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-[#2D4A3E]/5 rounded-lg border border-[#2D4A3E]/20">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-[#2D4A3E] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#2D4A3E] mb-1">Suggested Control</p>
                    <p className="text-sm text-[#4B5563] leading-relaxed">{risk.suggested_control}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Risk Matrix Legend */}
      <Card className="border-[#E5E1D8] mt-8" data-testid="risk-methodology">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Methodology</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
            Risks are automatically identified using rule-based logic that analyzes your ESG data against industry benchmarks, 
            regulatory thresholds, and best practices. Priority is calculated using Impact × Likelihood matrix.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#A33B3B]"></div>
              <span className="text-sm text-[#4B5563]">Critical: Immediate action required</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#C85A32]"></div>
              <span className="text-sm text-[#4B5563]">High: Address within 30 days</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-[#D5CABD]"></div>
              <span className="text-sm text-[#4B5563]">Medium: Include in annual plan</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ESGRisks;
